
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { businessInfo } from '@/data/businessInfo';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { title: 'Home', path: '/' },
    { title: 'About', path: '/about' },
    { title: 'Services', path: '/services' },
    { title: 'Locations', path: '/locations' },
    { title: 'Portfolio', path: '/portfolio' },
    { title: 'Blog', path: '/blog' },
    { title: 'Contact', path: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close mobile menu when changing routes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-in-out',
        isScrolled
          ? 'bg-white/90 backdrop-blur-md shadow-md py-3'
          : 'bg-black/40 backdrop-blur-sm py-5'
      )}
    >
      <div className="container mx-auto px-4 flex justify-between items-center">
        {/* Logo */}
        <Link to="/" className="relative z-10">
          <div className="flex items-center">
            <span className={cn(
              "text-2xl font-bold transition-colors duration-300",
              isScrolled ? "text-pkgreen-600" : "text-pkgreen-400"
            )}>
              PK Landscape Associates
            </span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={cn(
                'transition-colors duration-300 font-medium',
                isScrolled 
                  ? 'text-gray-800 hover:text-pkgreen-600' 
                  : 'text-white hover:text-pkgreen-400',
                location.pathname === link.path && (isScrolled ? 'text-pkgreen-600' : 'text-pkgreen-400 font-semibold')
              )}
            >
              {link.title}
            </Link>
          ))}
          <Link
            to="/client-portal"
            className={cn(
              "ml-4 font-medium py-2 px-4 rounded-md transition-all duration-300 ease-in-out",
              isScrolled
                ? "bg-pkgreen-600 hover:bg-pkgreen-700 text-white"
                : "bg-pkgreen-500 hover:bg-pkgreen-600 text-white"
            )}
          >
            Client Portal
          </Link>
        </nav>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className={cn(
            "lg:hidden relative z-10",
            isScrolled ? "text-gray-800" : "text-white"
          )}
          aria-label="Toggle Menu"
        >
          {isMenuOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <Menu className="h-6 w-6" />
          )}
        </button>

        {/* Mobile Navigation */}
        <div
          className={cn(
            'fixed inset-0 bg-white lg:hidden transition-transform duration-300 ease-in-out z-40',
            isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          )}
        >
          <div className="flex flex-col h-full pt-24 px-8">
            <nav className="flex flex-col space-y-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={cn(
                    'text-gray-800 hover:text-pkgreen-600 text-xl font-serif flex items-center',
                    location.pathname === link.path && 'text-pkgreen-600 font-semibold'
                  )}
                >
                  {link.title}
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Link>
              ))}
              <Link
                to="/client-portal"
                className="bg-pkgreen-600 hover:bg-pkgreen-700 text-white font-medium py-3 px-6 rounded-md mt-4 inline-block text-center"
              >
                Client Portal
              </Link>
            </nav>
            
            <div className="mt-auto mb-12">
              <p className="text-gray-600 mb-4">Serving Fairfield County since 2010</p>
              <a 
                href={`tel:${businessInfo.phoneInternational}`}
                className="text-pkgreen-600 font-medium text-lg"
              >
                {businessInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
